// =============================================================================
// themes/variation — deterministyczna wariacja wygladu per domena (footprint).
//
// Seed = sha256(domain). Z bajtow seeda losujemy: odcien, promien, skale fontow,
// PREFIKS KLAS CSS (kazda domena ma inne nazwy klas), kolejnosc blokow na home,
// wariant kart, liczbe wpisow na strone, format daty, stos fontow systemowych.
// Ta sama domena -> zawsze ten sam wyglad. Dwie domeny -> rozne. Zero AI.
// =============================================================================

import { sha256 } from "@noble/hashes/sha256";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils";

export type HomeBlock = "hero" | "latest" | "categories" | "about";
export type CardVariant = "a" | "b" | "c";
export type DateFormat = "long" | "numeric" | "short";
export type NavStyle = "left" | "centered" | "split";

export interface Variation {
  seed: string;
  hue: number;
  accentHue: number;
  /** 0..1 — jak bardzo neutralne szarosci sa zabarwione odcieniem */
  neutralTint: number;
  radius: number;
  fontScale: number;
  /** np. "tk-a3f-" — prefiks kazdej klasy CSS w motywie */
  classPrefix: string;
  blockOrder: HomeBlock[];
  cardVariant: CardVariant;
  postsPerPage: number;
  dateFormat: DateFormat;
  navStyle: NavStyle;
  fontStack: { body: string; heading: string };
  /** Szerokosc kontenera w px */
  maxWidth: number;
}

// Same stosy systemowe (zero zewnetrznych hostow). Body ZAWSZE bezszeryfowe —
// czytelnosc; naglowki: 3 bezszeryfowe + 1 szeryfowy (Georgia) jako akcent.
const FONT_STACKS: Array<{ body: string; heading: string }> = [
  {
    body: "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    heading: "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  {
    body: "'Segoe UI', system-ui, -apple-system, Roboto, Arial, sans-serif",
    heading: "'Segoe UI Semibold', 'Segoe UI', system-ui, Roboto, Arial, sans-serif",
  },
  {
    body: "system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif",
    heading: "Georgia, 'Times New Roman', Times, serif",
  },
  {
    body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    heading: "'Trebuchet MS', 'Segoe UI', Tahoma, Arial, sans-serif",
  },
  {
    body: "ui-sans-serif, system-ui, 'Segoe UI', Roboto, sans-serif",
    heading: "ui-sans-serif, system-ui, 'Segoe UI', Roboto, sans-serif",
  },
];

const BLOCK_ORDERS: HomeBlock[][] = [
  ["hero", "latest", "categories", "about"],
  ["hero", "categories", "latest", "about"],
  ["latest", "hero", "categories", "about"],
  ["hero", "latest", "about", "categories"],
  ["latest", "categories", "hero", "about"],
  ["hero", "about", "latest", "categories"],
];

export function seedForDomain(domain: string): string {
  return bytesToHex(sha256(utf8ToBytes(domain.trim().toLowerCase())));
}

/** Wyciaga bajt seeda z pozycji i (seed hex, 64 znaki = 32 bajty). */
function byteAt(seed: string, i: number): number {
  const idx = (i * 2) % seed.length;
  return parseInt(seed.slice(idx, idx + 2), 16) || 0;
}

export function variationFromSeed(seedHex: string, brandHueOverride?: number | null): Variation {
  const seed = /^[0-9a-f]{64}$/i.test(seedHex) ? seedHex.toLowerCase() : seedForDomain(seedHex || "default");
  const b = (i: number) => byteAt(seed, i);

  const hue = brandHueOverride != null && Number.isFinite(brandHueOverride)
    ? Math.round(((brandHueOverride % 360) + 360) % 360)
    : Math.round((b(0) / 255) * 359);
  const accentHue = (hue + 120 + Math.round((b(1) / 255) * 120)) % 360;
  const radiusOptions = [0, 4, 6, 8, 12, 16];
  const scaleOptions = [1, 1.04, 1.08, 1.12];
  const dateOptions: DateFormat[] = ["long", "numeric", "short"];
  const cardOptions: CardVariant[] = ["a", "b", "c"];
  const navOptions: NavStyle[] = ["left", "centered", "split"];
  const widthOptions = [720, 760, 800, 860, 920];

  return {
    seed,
    hue,
    accentHue,
    neutralTint: Math.round((b(2) / 255) * 40) / 100, // 0..0.4
    radius: radiusOptions[b(3) % radiusOptions.length],
    fontScale: scaleOptions[b(4) % scaleOptions.length],
    classPrefix: `tk-${seed.slice(0, 3)}-`,
    blockOrder: BLOCK_ORDERS[b(5) % BLOCK_ORDERS.length],
    cardVariant: cardOptions[b(6) % cardOptions.length],
    postsPerPage: 8 + (b(7) % 5), // 8..12
    dateFormat: dateOptions[b(8) % dateOptions.length],
    navStyle: navOptions[b(9) % navOptions.length],
    fontStack: FONT_STACKS[b(10) % FONT_STACKS.length],
    maxWidth: widthOptions[b(11) % widthOptions.length],
  };
}

/** #rrggbb -> odcien HSL (0..359) albo null gdy smiec. */
export function hueFromHex(hex: string | null | undefined): number | null {
  const raw = (hex || "").trim().replace(/^#/, "");
  const full = raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw;
  if (!/^[0-9a-f]{6}$/i.test(full)) return null;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const bl = parseInt(full.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, bl);
  const min = Math.min(r, g, bl);
  const d = max - min;
  if (d === 0) return null; // szarosc — brak odcienia, zostaw losowy
  let h = 0;
  if (max === r) h = ((g - bl) / d) % 6;
  else if (max === g) h = (bl - r) / d + 2;
  else h = (r - g) / d + 4;
  h = Math.round(h * 60);
  return (h + 360) % 360;
}
